import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';

const TAB_GUIDE = 'Your 60 Seconds IP Guide';
const TAB_THOUGHTS = 'The Penguin Thoughts';

/** Parse location hash into tab + optional article id. */
function parseHash(hash) {
  const raw = (hash || '').replace(/^#/, '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  const parts = path.split('/').filter(Boolean);

  if (parts[0] === 'guide') {
    return { tab: TAB_GUIDE, articleId: null };
  }
  if (parts[0] === 'thoughts') {
    const articleId =
      parts[1] != null && String(parts[1]).length > 0 ? String(parts[1]) : null;
    return { tab: TAB_THOUGHTS, articleId };
  }
  // `#/`, empty, or unknown → videos / guide tab
  return { tab: TAB_GUIDE, articleId: null };
}

function hashForGuide() {
  return '#/guide';
}

function hashForThoughts(articleId) {
  if (articleId != null && articleId !== '') {
    return `#/thoughts/${articleId}`;
  }
  return '#/thoughts';
}

/** Format a publish date for display (Asia/Singapore-friendly long form). */
function formatDate(dateStr) {
  try {
    return new Date(dateStr).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/** Flatten React children to plain text (for Disclaimer detection). */
function childrenToText(children) {
  if (children == null || children === false) return '';
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(childrenToText).join('');
  }
  if (React.isValidElement(children) && children.props) {
    return childrenToText(children.props.children);
  }
  return '';
}

/**
 * Limited markdown component map — no raw HTML, no images/tables.
 * Headings: #→h2, ##→h3, ###/####→h4 with article-heading class.
 */
const markdownComponents = {
  h1: ({ children }) => (
    <h2 className="article-heading">{children}</h2>
  ),
  h2: ({ children }) => (
    <h3 className="article-heading">{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 className="article-heading">{children}</h4>
  ),
  h4: ({ children }) => (
    <h4 className="article-heading">{children}</h4>
  ),
  p: ({ children }) => {
    const text = childrenToText(children).trim();
    const isDisclaimer = /^Disclaimer\s*:/i.test(text);
    return (
      <p className={isDisclaimer ? 'article-disclaimer' : 'article-paragraph'}>
        {children}
      </p>
    );
  },
  ul: ({ children }) => <ul className="article-list">{children}</ul>,
  ol: ({ children }) => <ol className="article-list">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

const MARKDOWN_ALLOWED = [
  'h1',
  'h2',
  'h3',
  'h4',
  'p',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'a',
];

/** Card excerpt: light markdown strip, then truncate by words. */
function getExcerpt(content, wordLimit = 36) {
  if (!content) return '';
  const plain = content
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\n+/g, ' ')
    .trim();
  const words = plain.split(/\s+/).filter(Boolean);
  if (words.length <= wordLimit) return plain;
  return words.slice(0, wordLimit).join(' ') + '…';
}


function TagChip({ tag, asButton = false, active = false, onClick }) {
  if (asButton) {
    return (
      <button
        type="button"
        className={`tag-chip tag-chip--btn${active ? ' tag-chip--active' : ''}`}
        onClick={onClick}
      >
        {tag}
      </button>
    );
  }
  return <span className="tag-chip">{tag}</span>;
}

function ArticleBody({ content }) {
  return (
    <div className="article-body">
      <ReactMarkdown
        components={markdownComponents}
        allowedElements={MARKDOWN_ALLOWED}
        unwrapDisallowed
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
}

function App() {
  const initial = parseHash(
    typeof window !== 'undefined' ? window.location.hash : ''
  );
  const [activeTab, setActiveTab] = useState(initial.tab);
  const [routeArticleId, setRouteArticleId] = useState(initial.articleId);
  const [videos, setVideos] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [articlesStatus, setArticlesStatus] = useState('loading'); // loading | ready | error
  const [videosStatus, setVideosStatus] = useState('loading');
  const [linkCopied, setLinkCopied] = useState(false);

  const syncFromHash = useCallback(() => {
    const { tab, articleId } = parseHash(window.location.hash);
    setActiveTab(tab);
    setRouteArticleId(tab === TAB_THOUGHTS ? articleId : null);
    if (tab !== TAB_THOUGHTS) {
      setSelectedTag(null);
    }
  }, []);

  useEffect(() => {
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [syncFromHash]);

  useEffect(() => {
    setVideosStatus('loading');
    fetch('/videos.json')
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load videos');
        return response.json();
      })
      .then((data) => {
        setVideos(data);
        setVideosStatus('ready');
      })
      .catch(() => setVideosStatus('error'));

    setArticlesStatus('loading');
    fetch('/articles.json')
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load articles');
        return response.json();
      })
      .then((data) => {
        const sortedArticles = [...data].sort(
          (a, b) => new Date(b.publishDate) - new Date(a.publishDate)
        );
        setArticles(sortedArticles);
        const tags = [...new Set(data.flatMap((article) => article.tags || []))].sort();
        setAllTags(tags);
        setArticlesStatus('ready');
      })
      .catch(() => setArticlesStatus('error'));
  }, []);

  // Resolve selected article from hash once articles are ready
  useEffect(() => {
    if (activeTab !== TAB_THOUGHTS) {
      setSelectedArticle(null);
      return;
    }
    if (routeArticleId == null) {
      setSelectedArticle(null);
      return;
    }
    if (articlesStatus !== 'ready') return;

    const found = articles.find((a) => String(a.id) === String(routeArticleId));
    setSelectedArticle(found || null);
    if (found) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab, routeArticleId, articles, articlesStatus]);

  const filteredArticles = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return articles
      .filter((article) => (selectedTag ? (article.tags || []).includes(selectedTag) : true))
      .filter((article) => {
        if (!q) return true;
        const hay = `${article.title} ${(article.tags || []).join(' ')} ${article.content}`.toLowerCase();
        return hay.includes(q);
      });
  }, [articles, selectedTag, searchTerm]);

  const navigateTab = (tab) => {
    if (tab === TAB_GUIDE) {
      window.location.hash = hashForGuide();
    } else {
      window.location.hash = hashForThoughts(null);
    }
  };

  const openArticle = (article) => {
    window.location.hash = hashForThoughts(article.id);
  };

  const backToList = () => {
    window.location.hash = hashForThoughts(null);
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.hash}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
    }
  };

  return (
    <div className="app">
      <header className="banner" role="banner" style={{ backgroundImage: 'url(/banner.jpg)' }} />

      <nav className="main-nav" aria-label="Primary">
        {[TAB_GUIDE, TAB_THOUGHTS].map((tab) => (
          <button
            key={tab}
            type="button"
            className={`nav-tab${activeTab === tab ? ' nav-tab--active' : ''}`}
            onClick={() => navigateTab(tab)}
          >
            {tab}
          </button>
        ))}
      </nav>


      <main className="content-panel">
        <h2 className="section-title">{activeTab}</h2>

        {activeTab === TAB_GUIDE ? (
          <div className="videos-section">
            {videosStatus === 'loading' && (
              <p className="state-msg">Loading videos…</p>
            )}
            {videosStatus === 'error' && (
              <p className="state-msg state-msg--error">
                Couldn’t load videos. Please refresh and try again.
              </p>
            )}
            {videosStatus === 'ready' && videos.length === 0 && (
              <p className="state-msg">No videos yet. Check back soon.</p>
            )}
            {videosStatus === 'ready' && videos.length > 0 && (
              <div className="videos-grid">
                {videos.map((video) => (
                  <div key={video.id} className="video-card">
                    <iframe
                      width="100%"
                      height="356"
                      src={`https://www.youtube.com/embed/${video.id}`}
                      title={video.title}
                      frameBorder="0"
                      allowFullScreen
                      className="video-iframe"
                    />
                    <div className="video-card-body">
                      <h3 className="video-card-title">{video.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="articles-layout">
            <div className="articles-main">
              {articlesStatus === 'loading' && (
                <p className="state-msg">Loading articles…</p>
              )}
              {articlesStatus === 'error' && (
                <p className="state-msg state-msg--error">
                  Couldn’t load articles. Please refresh and try again.
                </p>
              )}

              {articlesStatus === 'ready' && selectedArticle && (
                <article className="article-detail">
                  <div className="article-detail-toolbar">
                    <button
                      type="button"
                      className="btn-back"
                      onClick={backToList}
                    >
                      ← Back to Articles
                    </button>
                    <button
                      type="button"
                      className="btn-copy-link"
                      onClick={copyShareLink}
                      aria-live="polite"
                    >
                      {linkCopied ? 'Link copied!' : 'Copy link'}
                    </button>
                  </div>
                  <header className="article-detail-header">
                    <h3 className="article-detail-title">{selectedArticle.title}</h3>
                    <p className="article-detail-meta">
                      <time dateTime={selectedArticle.publishDate}>
                        {formatDate(selectedArticle.publishDate)}
                      </time>
                    </p>
                    <div className="tag-row">
                      {(selectedArticle.tags || []).map((tag) => (
                        <TagChip key={tag} tag={tag} />
                      ))}
                    </div>
                  </header>
                  <ArticleBody content={selectedArticle.content} />
                </article>
              )}

              {articlesStatus === 'ready' && !selectedArticle && (
                <div className="articles-list-view">
                  {routeArticleId != null && (
                    <p className="state-msg">
                      That article wasn’t found. Showing all articles instead.
                    </p>
                  )}
                  <div className="tag-filter" role="group" aria-label="Filter by tag">
                    <TagChip
                      tag="All"
                      asButton
                      active={!selectedTag}
                      onClick={() => setSelectedTag(null)}
                    />
                    {allTags.map((tag) => (
                      <TagChip
                        key={tag}
                        tag={tag}
                        asButton
                        active={selectedTag === tag}
                        onClick={() => setSelectedTag(tag)}
                      />
                    ))}
                  </div>

                  {filteredArticles.length === 0 ? (
                    <p className="state-msg">
                      {articles.length === 0
                        ? 'No articles published this week yet. Check back Monday.'
                        : `No articles match your search${selectedTag ? ` or the “${selectedTag}” filter` : ''}. Try a different term or clear filters.`}
                    </p>
                  ) : (
                    <ul className="article-cards">
                      {filteredArticles.map((article) => (
                        <li key={article.id}>
                          <article
                            className="article-card"
                            onClick={() => openArticle(article)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openArticle(article);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <h3 className="article-card-title">{article.title}</h3>
                            <p className="article-card-date">
                              <time dateTime={article.publishDate}>
                                {formatDate(article.publishDate)}
                              </time>
                            </p>
                            <div className="tag-row">
                              {(article.tags || []).map((tag) => (
                                <TagChip key={tag} tag={tag} />
                              ))}
                            </div>
                            <p className="article-card-excerpt">
                              {getExcerpt(article.content)}
                            </p>
                            <span className="article-card-cta">Read more →</span>
                          </article>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <aside className="articles-sidebar">
              <h4 className="sidebar-heading">Search</h4>
              <label className="sr-only" htmlFor="article-search">
                Search articles
              </label>
              <input
                id="article-search"
                type="search"
                className="search-input"
                placeholder="Search articles…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={articlesStatus !== 'ready'}
              />
              {articlesStatus === 'ready' && (
                <ul className="sidebar-list">
                  {filteredArticles.length === 0 ? (
                    <li className="sidebar-empty">
                      {articles.length === 0 ? 'None this week' : 'No matches'}
                    </li>
                  ) : (
                    filteredArticles.map((article) => (
                      <li key={article.id}>
                        <button
                          type="button"
                          className={`sidebar-item${
                            selectedArticle?.id === article.id ? ' sidebar-item--active' : ''
                          }`}
                          onClick={() => openArticle(article)}
                        >
                          {article.title}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </aside>
          </div>
        )}
      </main>

      <footer className="site-footer">
        <h3 className="footer-heading">Follow Us on Our Socials</h3>
        <div className="social-links">
          <a
            href="https://www.youtube.com/@IpPenguin"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <i className="fi fi-brands-youtube" aria-hidden="true" /> @IpPenguin
          </a>
          <a
            href="https://www.facebook.com/people/IP-Penguin/61580529398991/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <i className="fi fi-brands-facebook" aria-hidden="true" /> IP-Penguin
          </a>
          <a
            href="https://www.instagram.com/ip_penguin/"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <i className="fi fi-brands-instagram" aria-hidden="true" /> @ip_penguin
          </a>
          <a
            href="https://www.tiktok.com/@ip.penguin"
            target="_blank"
            rel="noopener noreferrer"
            className="social-link"
          >
            <i className="fi fi-brands-tik-tok" aria-hidden="true" /> @ip.penguin
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;
